import React, { useEffect, useLayoutEffect, useRef, useState } from 'react'

type Props = {
    anchorEl: HTMLElement | null
    open: boolean
    onClose: () => void
    minWidth?: number
    maxHeight?: number // используем как FIXED height
    zIndex?: number
    children: React.ReactNode
    closeOnInsidePointerDown?: boolean
}

const FixedDropdown: React.FC<Props> = ({
    anchorEl,
    open,
    onClose,
    minWidth = 320,
    maxHeight = 350, // ← фикс 350 по умолчанию
    zIndex = 60,
    children,
    closeOnInsidePointerDown,
}) => {
    const boxRef = useRef<HTMLDivElement>(null)
    const [pos, setPos] = useState<{
        top: number
        left: number
        width: number
    } | null>(null)

    const recalc = () => {
        if (!anchorEl) return
        const r = anchorEl.getBoundingClientRect()
        const vw = window.innerWidth
        const pad = 8 // отступы от краёв
        const gap = 8 // зазор от инпута

        // ширина: якоримся по левому краю, в экран не выходим
        let width = Math.max(minWidth, r.width)
        width = Math.min(width, vw - pad * 2)

        let left = r.left
        if (left + width > vw - pad) left = vw - pad - width
        if (left < pad) left = pad

        // ВСЕГДА ВВЕРХ — фиксированная высота (maxHeight)
        const top = Math.max(pad, r.top - gap - maxHeight)

        setPos({ top, left, width })
    }

    useLayoutEffect(() => {
        if (!open) return
        recalc()
        const id = requestAnimationFrame(recalc)
        return () => cancelAnimationFrame(id)
    }, [open, anchorEl, maxHeight, minWidth])

    useEffect(() => {
        if (!open) return
        const handler = () => recalc()
        document.addEventListener('scroll', handler, true)
        window.addEventListener('resize', handler)
        return () => {
            document.removeEventListener('scroll', handler, true)
            window.removeEventListener('resize', handler)
        }
    }, [open])

    useEffect(() => {
        if (!open) return
        const onDocPointerDown = (e: PointerEvent) => {
            const path = e.composedPath()
            if (
                (boxRef.current && path.includes(boxRef.current)) ||
                (anchorEl && path.includes(anchorEl))
            )
                return
            onClose()
        }
        document.addEventListener('pointerdown', onDocPointerDown, true)
        return () =>
            document.removeEventListener('pointerdown', onDocPointerDown, true)
    }, [open, anchorEl, onClose])

    if (!open || !pos) return null

    return (
        <div
            ref={boxRef}
            style={{
                position: 'fixed',
                top: pos.top,
                left: pos.left,
                width: pos.width,
                height: maxHeight, // ← фиксированная высота
                maxHeight: maxHeight, // на всякий
                zIndex,
                pointerEvents: 'auto',
                boxSizing: 'border-box',
                overflowY: 'auto', // скролл внутри
            }}
            className="rounded-md shadow-lg bg-[#f6f6f6] dark:bg-[#2A282B] border border-[#a0a1a3] dark:border-[#333033] custom-scrollbar"
            onPointerDown={(e) => {
                if (!closeOnInsidePointerDown) {
                    e.stopPropagation()
                    return
                }

                const target = e.target as HTMLElement
                const clickedButton = target.closest('button')

                // если клик НЕ по кнопке — считаем "ничего не выбрали", закрываем
                if (!clickedButton) onClose()
            }}
        >
            {children}
        </div>
    )
}

export default FixedDropdown
